"use strict";

import { fixIosSvgFilters } from "@/utils/svgUtils";



/**
 * Creates a Vue component for V2 fighter designs.
 * Supports dynamic dynamic colors via CSS variables and single-integer number inputs.
 * @param {string} fighterName
 */
export async function createFighterComponentV2(fighterName) {
    let [template, colors] = await createFighterTemplate(fighterName);

    let component = {
        template: template,
        props: {
            number: { type: [Number, String], default: 1 },
            qrVisible: { type: Boolean, default: false },
            roundelScale: { type: Number, default: 2.0 },
        },
        data() {
            return {
                colors, // Reactive color object { accent: '#...', ... }
                uid: "",
            };
        },
        computed: {
            cssStyle() {
                const styles = {};
                if (this.colors) {
                    for (const [key, value] of Object.entries(this.colors)) {
                        styles[`--${key}`] = value;
                    }
                }
                return styles;
            },
            numLUrl() {
                return `#numL${this.number}_${this.uid}`;
            },
            numRUrl() {
                return `#numR${this.number}_${this.uid}`;
            },
        },
        created() {
            this.uid = Math.random().toString(36).substr(2, 9);
            console.log("fighterComponentV2 created");
        },
        methods: {
            setTarget(target) {
                this.$emit("openColorInput", target);
            },
            qrCodePath(fighter) {
                return require(`@/assets/qrCodes/${fighter}.svg`);
            },
        },
    };

    return {
        component,
        colors, // return initial defaults
    };
}

const fighters = require.context("@/assets/fighters", false, /\.svg$/);

function fighterAssetPath(fighter) {
    return fighters("./" + fighter + ".svg");
}

async function createFighterTemplate(fighterName) {
    const svgPath = fighterAssetPath(fighterName);
    const response = await fetch(svgPath);
    let svgText = await response.text();

    const colors = {};
    const classStyles = {};

    // 1. Extract CSS variables (Defaults) AND Process Style Block Logic
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/i;
    const match = styleRegex.exec(svgText);

    if (match) {
        const fullStyleTag = match[0];
        let styleContent = match[1];

        // A. Extract vars from :root (or anywhere)
        const varRegex = /--([\w-]+):\s*([^;}]+)/g;
        let varMatch;
        while ((varMatch = varRegex.exec(styleContent)) !== null) {
            const varName = varMatch[1].trim();
            const varValue = varMatch[2].trim();
            colors[varName] = varValue;
        }

        // B. Extract Class Rules (.fold { ... })
        const classRegex = /\.([\w-]+)\s*{([^}]+)}/g;
        let classMatch;
        while ((classMatch = classRegex.exec(styleContent)) !== null) {
            const className = classMatch[1]; // e.g. "fold"
            const cssText = classMatch[2].trim().replace(/\s+/g, " "); // e.g. "fill:none;..."
            classStyles[className] = cssText;
        }

        // Remove style tag from svgText because we have consumed it
        svgText = svgText.replace(fullStyleTag, "");
    }

    // 2. Parse SVG to modify DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const svg = doc.getElementsByTagName("svg")[0];

    svg.style.width = "100%";
    svg.style.height = "100%";

    // Global iOS Fix for SVG Filters (V2)
    fixIosSvgFilters(svg);



    // 3. Transform & Inline Styles
    const allElements = svg.getElementsByTagName("*");
    for (let el of allElements) {
        // Collect all styles (Attribute + Class)
        let finalStyleString = el.getAttribute("style") || "";

        // Append Class Styles
        if (el.hasAttribute("class")) {
            const classes = el.getAttribute("class").split(/\s+/);
            classes.forEach(cls => {
                if (classStyles[cls]) {
                    if (finalStyleString && !finalStyleString.endsWith(";")) finalStyleString += ";";
                    finalStyleString += classStyles[cls];
                }
            });
        }

        // Also check 'fill' attribute if it uses var (move to explicit style for unified processing)
        const fillAttr = el.getAttribute("fill");
        if (fillAttr && fillAttr.includes("var(--")) {
            if (finalStyleString && !finalStyleString.endsWith(";")) finalStyleString += ";";
            finalStyleString += `fill:${fillAttr}`;
            el.removeAttribute("fill");
        }

        // Check 'stroke' attribute too
        const strokeAttr = el.getAttribute("stroke");
        if (strokeAttr && strokeAttr.includes("var(--")) {
            if (finalStyleString && !finalStyleString.endsWith(";")) finalStyleString += ";";
            finalStyleString += `stroke:${strokeAttr}`;
            el.removeAttribute("stroke");
        }

        if (!finalStyleString) continue;

        // Parse styles
        const props = finalStyleString.split(';').filter(s => s.trim());
        const staticStyles = [];
        const dynamicStyles = {};
        let hasDynamic = false;

        props.forEach(prop => {
            const parts = prop.split(':');
            if (parts.length < 2) return;
            const key = parts[0].trim();
            const val = parts.slice(1).join(':').trim();

            const varMatch = val.match(/var\(--([^)]+)\)/);
            if (varMatch) {
                // It's dynamic
                const varName = varMatch[1];
                dynamicStyles[key] = `colors['${varName}']`;
                hasDynamic = true;

                // Add click handler for this var (simple last-write-wins)
                el.setAttribute("v-on:click.prevent", `setTarget("${varName}")`);
            } else {
                // Static
                staticStyles.push(`${key}:${val}`);
            }
        });

        // Apply
        if (staticStyles.length > 0) {
            el.setAttribute("style", staticStyles.join(';'));
        } else {
            el.removeAttribute("style");
        }

        if (hasDynamic) {
            let styleObjParts = [];
            for (const [key, val] of Object.entries(dynamicStyles)) {
                styleObjParts.push(`'${key}': ${val}`);
            }
            el.setAttribute(":style", `{ ${styleObjParts.join(', ')} }`);
        }
    }

    // 4. Setup Number bindings
    const numL = svg.getElementById("numL");
    if (numL) {
        numL.removeAttribute("xlink:href");
        numL.setAttribute("v-bind:xlink:href", "numLUrl");
    }

    const numR = svg.getElementById("numR");
    if (numR) {
        numR.removeAttribute("xlink:href");
        numR.setAttribute("v-bind:xlink:href", "numRUrl");
    }

    // 5. Scoping IDs for Print Support (Same logic as V1)
    const idElements = svg.getElementsByTagName("*");
    const ids = new Set();
    for (let i = 0; i < idElements.length; i++) {
        if (idElements[i].id) {
            ids.add(idElements[i].id);
        }
    }

    for (let i = 0; i < idElements.length; i++) {
        const el = idElements[i];

        // Update fill="url(#id)"
        const fill = el.getAttribute("fill");
        if (fill && fill.startsWith("url(#")) {
            const id = fill.substring(5, fill.length - 1);
            if (ids.has(id)) {
                el.removeAttribute("fill");
                el.setAttribute("v-bind:fill", "`url(#${" + "'" + id + "_' + uid})`");
            }
        }

        // Update stroke="url(#id)"
        const stroke = el.getAttribute("stroke");
        if (stroke && stroke.startsWith("url(#")) {
            const id = stroke.substring(5, stroke.length - 1);
            if (ids.has(id)) {
                el.removeAttribute("stroke");
                el.setAttribute("v-bind:stroke", "`url(#${" + "'" + id + "_' + uid})`");
            }
        }

        // Update xlink:href="#id"
        // Note: numL/numR are already handled by bindings above, so this loop mainly hits others
        const href = el.getAttribute("xlink:href");
        if (href && href.startsWith("#")) {
            const id = href.substring(1);
            if (ids.has(id)) {
                el.removeAttribute("xlink:href");
                el.setAttribute("v-bind:xlink:href", "`#${" + "'" + id + "_' + uid}`");
            }
        }

        // Update the ID definition itself
        if (el.id && ids.has(el.id)) {
            const oldId = el.id;
            el.removeAttribute("id");
            el.setAttribute("v-bind:id", `'${oldId}_' + uid`);
        }
    }

    // Serializing back to string for Vue template
    const template = svg.outerHTML;
    console.log("[V2] Final Template Size:", template.length);

    return [template, colors];
}
